import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';

export const useAppConfig = () => {
  const { featureFlags, appConfig } = useConfigStore.getState();

  // ================= Merge function =================
  const mergeConfigs = (base: any, platformOverride: any): any => {
    if (!platformOverride) return base;
    if (typeof base !== 'object' || base === null) return platformOverride;
    if (typeof platformOverride !== 'object' || platformOverride === null) return platformOverride;

    const merged: any = { ...base };
    for (const key of Object.keys(platformOverride)) {
      if (key in base && typeof base[key] === 'object' && typeof platformOverride[key] === 'object') {
        merged[key] = mergeConfigs(base[key], platformOverride[key]);
      } else {
        merged[key] = platformOverride[key];
      }
    }
    return merged;
  };

  // ================= Merge global config with web overrides =================
  const platformWebOverride = featureFlags?.platforms?.web || {};
  const mergedFlags = mergeConfigs(featureFlags, platformWebOverride);

  // ================= Helper for web override with fallback =================
  const withWebOverride = (webVal: any, defaultVal: any) => (webVal !== undefined ? webVal : defaultVal);

  // ================= Derived authentication / subscription =================
  const authentication_type = mergedFlags?.common?.authentication_type?.toLowerCase();
  const subscription_type = mergedFlags?.common?.subscription_type?.toLowerCase();

  const cleengSub = mergedFlags?.cleeng?.subscription || {};

  const isAnalyticsEnable = mergedFlags?.feature_flags?.is_analytics_enabled;

  //Upload Feature
  const uploadSettings = mergedFlags?.feature_flags?.upload_settings;
  const isUploadEnabled = uploadSettings?.is_upload_enabled ?? false;
  const isReelUploadEnabled = uploadSettings?.is_reel_upload_enabled ?? isUploadEnabled;
  const isVideoUploadEnabled = uploadSettings?.is_video_upload_enabled ?? isUploadEnabled;
  const isModerateContentEnabled = uploadSettings?.is_moderate_content_enabled ?? isUploadEnabled;
  const multipartUploadChunkSize = uploadSettings?.multipart_upload_chunk_size ?? 15;
  const thumbnailSizeLimit = uploadSettings?.thumbnail_size ?? 50;

  // ================= Return organized config =================

  return {
    // Common
    byDefaultLanguage: mergedFlags?.common?.default_language,
    isClientActive: withWebOverride(platformWebOverride?.is_client_active, mergedFlags?.common?.is_client_active),
    isMultiLanguageSupport: mergedFlags?.feature_flags?.is_multi_language_support,
    isSupportEmail: mergedFlags?.feature_flags?.is_support_email,
    isGoogleCaptchaEnabled: mergedFlags?.feature_flags?.is_google_captcha_enabled,

    // Authentication
    isAuthenticationEnabled: mergedFlags?.authentication?.is_authentication_enabled,
    isQrAuthenticationEnabled: mergedFlags?.authentication?.is_qr_authentication_enabled,
    isGuestModeEnabled: mergedFlags?.authentication?.is_guest_mode_enabled,
    isSocialLoginEnabled: withWebOverride(platformWebOverride?.is_social_login_enabled, mergedFlags?.authentication?.is_social_login_enabled),
    isShowLoginButton: withWebOverride(platformWebOverride?.can_show_signin_button, mergedFlags?.authentication?.can_show_signin_button),
    isShowRegisterButton: withWebOverride(platformWebOverride?.can_show_signup_button, mergedFlags?.authentication?.can_show_signup_button),
    qrSettings: mergedFlags?.authentication?.qr_settings,

    // Authentication / Subscription Derived
    authentication_type,
    subscription_type,
    isCleeng: authentication_type === 'cleeng',
    isInplayer: authentication_type === 'inplayer',
    isCognitoEnabled: authentication_type === 'cognito',
    isKatonCognitoEnabled: authentication_type === 'katon_cognito',
    isCustomApiEnabled: authentication_type === 'cognito' || authentication_type === 'katon_cognito' || authentication_type === 'custom_backend',
    isInplayerSubscriptionEnabled: subscription_type === 'inplayer',
    isCleengSubscriptionEnabled: subscription_type === 'cleeng',
    isRenewableEnabled: cleengSub?.is_renewable_enabled,
    isSubscriptionSwitch: cleengSub?.is_subscription_change_allowed,

    // Analytics
    isAnalyticsEnable: isAnalyticsEnable,
    isGoogleAnalytics: isAnalyticsEnable ? mergedFlags?.feature_flags?.analytics_provider?.google : false,
    isFirebaseAnalytics: isAnalyticsEnable ? mergedFlags?.feature_flags?.analytics_provider?.firebase : false,
    isAppsFlyerAnalytics: isAnalyticsEnable ? mergedFlags?.feature_flags?.analytics_provider?.appsflyer : false,
    isAdobeAnalytics: isAnalyticsEnable ? mergedFlags?.feature_flags?.analytics_provider?.adobe : false,
    adobeScripTag: mergedFlags?.feature_flags?.settings?.analytics_settings?.abobe?.script_tag,
    gtmId: platformWebOverride?.gtm_id || '',
    system73AccessKey: appConfig?.system73?.system73AccessKey,

    //UTM
    isUtmEnabled: appConfig?.feature?.isUtmEnabled,

    // Playback & Player
    isAutoPlay: withWebOverride(platformWebOverride?.is_autoplay_enabled, mergedFlags?.is_autoplay_enabled),
    autoplayDefaultValue: mergedFlags?.feature_flags?.settings?.autoplay_default_value,
    isVideoQualityEnabled: mergedFlags?.feature_flags?.settings?.is_video_quality_enabled,
    isContinueWatchingEnabled: mergedFlags?.feature_flags?.is_continue_watching_enabled,
    isRelatedVideos: mergedFlags?.feature_flags?.is_recommended_content_enabled,
    playerType: mergedFlags?.player_type,

    // Ads
    isAdEnabled: mergedFlags?.feature_flags?.ad_enabled,
    adConfigLevel: mergedFlags?.feature_flags?.ad_settings?.ad_config_level,
    featuredAdDeliveryMethod: mergedFlags?.feature_flags?.ad_settings?.ad_delivery_method,

    // Legal
    canShowTermsOfUse: mergedFlags?.feature_flags?.settings?.is_terms_of_use_enabled,
    canShowPrivacyPolicy: mergedFlags?.feature_flags?.settings?.is_privacy_policy_enabled,
    canShowAboutUs: mergedFlags?.feature_flags?.settings?.is_about_us_enabled,
    privacyPolicyPlacement: mergedFlags?.common?.legal_docs?.placement?.privacy_policy,
    termsOfUsePlacement: mergedFlags?.common?.legal_docs?.placement?.terms_of_use,
    aboutUsPlacement: mergedFlags?.common?.legal_docs?.placement?.about_us,
    helpPlacement: mergedFlags?.common?.legal_docs?.placement?.help,

    // Settings / UI
    canShowSettings: mergedFlags?.feature_flags?.settings?.is_settings_enable,
    isSwitchProfileEnable: mergedFlags?.feature_flags?.settings?.is_switch_profile_enabled,
    isSearchEnabled: mergedFlags?.feature_flags?.is_search_enabled,
    heroBannerSize: platformWebOverride?.ui_settings?.hero_carousel || mergedFlags?.ui_settings?.hero_carousel,

    // EPG
    isEpgEnable: mergedFlags?.feature_flags?.is_epg_enabled,
    isCatchUpHoursEnabled: appConfig?.video_details?.epg?.is_catchup_hours_enabled,

    // Account Management
    canShowExportData: mergedFlags?.export_account,
    canDeleteAccount: mergedFlags?.delete_account,
    canChangePassword: mergedFlags?.change_password,
    isActivateTV: mergedFlags?.activate_tv,

    // Subscription
    isSubscriptionEnabled: mergedFlags?.feature_flags?.is_subscription_enabled,
    guestStreamingEnabled: mergedFlags?.feature_flags?.guest_streaming_enabled,
    showOffersAfterSignUp: mergedFlags?.feature_flags?.show_offers_after_signup,
    // Favorites
    isFavoriteEnable: mergedFlags?.feature_flags?.is_favorite_enabled,
    isSplitupFullname: mergedFlags?.ui_settings?.ui_flags?.is_splitup_fullname ?? false,
    isLastnameRequired: mergedFlags?.ui_settings?.ui_flags?.is_lastname_required ?? false,

    // DRM
    isDRMEnabled: mergedFlags?.feature_flags?.is_drm_enabled || false,

    //Upload Content
    isUploadEnabled,
    isReelUploadEnabled,
    isVideoUploadEnabled,
    isModerateContentEnabled,
    multipartUploadChunkSize,
    thumbnailSizeLimit,
  };
};
